import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useStores } from "../../../../use-stores";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import InventoryTagSelect from "../../../components/Inventory/InventoryTagSelect";
import { PartnerCustomerInventoryTag } from "../../../../../../models/inventory/PartnerCustomerInventoryTag";
import { InventoryVariantUpdateRequest } from "../../../../../../models/inventory/InventoryAdmin";

type EditableMetaItem = {
  clientSku: string;
  productName?: string | null;
  title?: string | null;
  packSize?: number | null;
  boxSize?: string | null;
  tagId?: string | null;
  tag?: string | null;
  /** `undefined` when the caller does not know it (the field then cannot clear a threshold). */
  minThreshold?: number | null;
};

const MAX_MIN_THRESHOLD = 10_000_000;

interface EditItemMetaPopupProps {
  open: boolean;
  item: EditableMetaItem | null;
  tags: PartnerCustomerInventoryTag[];
  onClose: () => void;
  onSubmit: (payload: InventoryVariantUpdateRequest) => void;
  /** Enables "+ Add new tag" in the tag picker. */
  onCreateTag?: (
    tagId: string,
    tag: string,
  ) => Promise<PartnerCustomerInventoryTag>;
}

const EditItemMetaPopup: React.FC<EditItemMetaPopupProps> = observer(({
  open,
  item,
  tags,
  onClose,
  onSubmit,
  onCreateTag,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { userStore } = useStores();
  const canEditSku = userStore.hasPermission(EmployeeRoles.UPDATE_INVENTORY_SKU);
  const [clientSku, setClientSku] = useState("");
  const [packSize, setPackSize] = useState("");
  const [boxSize, setBoxSize] = useState("");
  const [tagId, setTagId] = useState("");
  const [tagLabel, setTagLabel] = useState("");
  const [minThreshold, setMinThreshold] = useState("");

  useEffect(() => {
    if (!open || !item) return;
    setClientSku(item.clientSku || "");
    setPackSize(
      item.packSize != null && Number.isFinite(item.packSize)
        ? String(item.packSize)
        : "",
    );
    setBoxSize(item.boxSize || "");
    setTagId(item.tagId || "");
    const matchedTag = tags.find(
      (tag) => String(tag.tag_id) === String(item.tagId || ""),
    );
    setTagLabel(matchedTag?.tag || item.tag || "");
    setMinThreshold(item.minThreshold != null ? String(item.minThreshold) : "");
    // Reset only when the popup opens or the item changes. `tags` is left out so that
    // creating a tag (which reloads the list) keeps the admin's edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const originalThreshold = item?.minThreshold ?? null;
  const thresholdRaw = minThreshold.trim();
  const parsedThreshold = thresholdRaw === "" ? null : Number(thresholdRaw);
  const hasInvalidThreshold =
    parsedThreshold !== null &&
    (!Number.isInteger(parsedThreshold) ||
      parsedThreshold < 1 ||
      parsedThreshold > MAX_MIN_THRESHOLD);
  // Clearing (empty field) is only sent when the item is known to have a threshold.
  const thresholdChanged =
    !hasInvalidThreshold &&
    (parsedThreshold !== null
      ? parsedThreshold !== originalThreshold
      : originalThreshold !== null);

  const handleSubmit = () => {
    try {
      const trimmedSku = clientSku.trim();
      if (!trimmedSku) {
        return;
      }

      const packSizeRaw = packSize.trim();
      const parsedPackSize =
        packSizeRaw === "" ? undefined : Number(packSizeRaw);

      const payload: InventoryVariantUpdateRequest = {
        clientSku: trimmedSku,
      };
      if (
        parsedPackSize != null &&
        Number.isFinite(parsedPackSize) &&
        Number.isInteger(parsedPackSize) &&
        parsedPackSize >= 0
      ) {
        payload.packSize = parsedPackSize;
      }
      if (boxSize.trim()) {
        payload.boxSize = boxSize.trim();
      }
      if (tagId.trim()) {
        payload.tagId = tagId.trim();
      }
      if (thresholdChanged) {
        payload.minThreshold = parsedThreshold;
      }

      onSubmit(payload);
    } catch (error) {
      console.error("Failed to submit item meta:", error);
    }
  };

  const packSizeRaw = packSize.trim();
  const hasInvalidPackSize =
    packSizeRaw !== "" &&
    (!Number.isFinite(Number(packSizeRaw)) ||
      !Number.isInteger(Number(packSizeRaw)) ||
      Number(packSizeRaw) < 0);
  const skuChanged = clientSku.trim() !== (item?.clientSku || "").trim();
  const canSubmit =
    clientSku.trim() !== "" &&
    !hasInvalidPackSize &&
    !hasInvalidThreshold &&
    (skuChanged ||
      packSizeRaw !== "" ||
      boxSize.trim() !== "" ||
      tagId.trim() !== "" ||
      thresholdChanged);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: { xs: 0, sm: "16px" },
            overflow: "hidden",
            boxShadow: {
              xs: "none",
              sm: "0 20px 40px rgba(0, 0, 0, 0.15)",
            },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
            height: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "90vh" },
            width: "100%",
            m: { xs: 0, sm: 2 },
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: { xs: "stretch", sm: "center" },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          maxHeight: { xs: "100%", sm: "90vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            flexShrink: 0,
          }}
        >
          <Box minWidth={0}>
            <CustomTypography variant="heading4" fontWeight={700}>
              Edit Item Meta
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              {canEditSku
                ? "Update SKU, pack size, box size, tag, and minimum threshold for this item"
                : "Update pack size, box size, tag, and minimum threshold for this SKU"}
            </CustomTypography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: { xs: 2, sm: 3 },
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  SKU
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    value={clientSku}
                    onChange={(e) => setClientSku(e.target.value)}
                    width="100%"
                    placeholder="Enter client SKU"
                    disabled={!canEditSku}
                  />
                </Box>
                {canEditSku && (
                  <CustomTypography
                    variant="body3"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Renames the client SKU. Catalog SKU, ledger, and shipment
                    lines are not rewritten.
                  </CustomTypography>
                )}
                <CustomTypography
                  variant="body3"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {item?.productName || item?.title || "—"}
                </CustomTypography>
              </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  PACK SIZE
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    type="number"
                    value={packSize}
                    onChange={(e) => setPackSize(e.target.value)}
                    width="100%"
                    placeholder="Enter pack size"
                    allowNegativeValue={false}
                    allowDecimalValue={false}
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  BOX SIZE
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    value={boxSize}
                    onChange={(e) => setBoxSize(e.target.value)}
                    width="100%"
                    placeholder="Enter box size"
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  TAG
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <InventoryTagSelect
                    tags={tags}
                    tagId={tagId}
                    tagLabel={tagLabel}
                    onChange={(nextTagId, nextTagLabel) => {
                      setTagId(nextTagId);
                      setTagLabel(nextTagLabel);
                    }}
                    onCreateTag={onCreateTag}
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  MIN THRESHOLD
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    type="number"
                    value={minThreshold}
                    onChange={(e) => setMinThreshold(e.target.value)}
                    width="100%"
                    placeholder="Not set"
                    allowNegativeValue={false}
                    allowDecimalValue={false}
                  />
                </Box>
                <CustomTypography
                  variant="body3"
                  color={hasInvalidThreshold ? "error" : "text.secondary"}
                  sx={{ mt: 1, display: "block" }}
                >
                  {hasInvalidThreshold
                    ? `Enter a whole number from 1 to ${MAX_MIN_THRESHOLD.toLocaleString()}.`
                    : "The client gets a low-stock alert (daily) when available stock falls below this. Leave empty for no alert."}
                </CustomTypography>
              </Paper>
            </Grid2>

            <Grid2
              size={12}
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
              sx={{ pt: 1 }}
            >
              <AppButton label="Cancel" onClick={onClose} variant="grayscale" />
              <AppButton
                label="Save"
                onClick={handleSubmit}
                variant="primary"
                disabled={!canSubmit}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Box>
    </Dialog>
  );
});

export default EditItemMetaPopup;
